import { Link as RouterLink } from "react-router-dom";
import { DateAndTimeGridCell } from "../common/DateAndTimeGridCell";
import CancelVehicleDeployment from "./CancelVehicleDeployment";
import { Button, ListItem, ListItemText, Tooltip } from "@mui/material";
import { JobExecutionStatus } from "./DeploymentStatus";
import { getLocalDateTime } from "../../services/Utils";
import { styled } from "@mui/material/styles";
import { tooltipClasses } from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import { JOB_EXECUTION_STATUS } from "../../services/Constants";

const JobExecutionStatusInput = (props) => {
  return (
    <>
      <TextField
        label="Value"
        variant="standard"
        style={{ minWidth: 120 }}
        select
        SelectProps={{
          native: true,
        }}
        onChange={(event) =>
          props.applyValue({ ...props.item, value: event.target.value })
        }
      >
        {Object.entries(JOB_EXECUTION_STATUS).map(([key, value]) => (
          <option key={key} value={value}>
            {value}
          </option>
        ))}
      </TextField>
    </>
  );
};

const getColumnsData = ({ vehicleId, deploymentId, deploymentVersion }) => {
   const customFilterForJobStatus = [
    {
      value: "equals",
      label: "Equals",
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.field || !filterItem.value || !filterItem.operator) {
          return null;
        }
        return (params) => {
          return params.value.toLowerCase() === filterItem.value.toLowerCase();
        };
      },
      InputComponent: JobExecutionStatusInput,
      InputComponentProps: { type: "string" },
    },
  ];
  const StatusTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.primary[50],
    },
    [`& .${tooltipClasses.tooltip}`]: {
      padding: theme.spacing(1),
      whiteSpace: "pre-wrap",
      fontSize: 12,
      color: theme.palette.common.black,
      backgroundColor: theme.palette.primary[50],
      border: "1px solid #dadde9",
    },
  }));

  const getJobExecutionStatusTitle = (cancelReason, canceledAt) => {
    let title = "";

    if (cancelReason) {
      title += `${cancelReason}\n`;
    }

    if (canceledAt) {
      title += `Job Canceled at: ${getLocalDateTime(canceledAt)}\n`;
    }

    return title;
  };

  return [
    {
      field: "serialNo",
      headerName: "Serial No",
      flex: 2,
      sortable: true,
      filterable: true,
      disableColumnMenu: false,
      renderCell: (vehicle) => {
        const { row = {} } = vehicle;
        if (vehicleId) {
          //Vehicle Deployment
          return row?.serialNo;
        } else {
          //Deployment Vehicle
          return (
            <Tooltip title={row.serialNo}>
              <Button
                size="medium"
                disabled={!row?.vehicleKey}
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                }}
                component={RouterLink}
                to={`/vehicles/${row.serialNo}/deployments/${deploymentId}`}
              >
                {row.serialNo}
              </Button>
            </Tooltip>
          );
        }
      },
    },
    {
      field: "vehicleKey",
      headerName: "Vehicle Key",
      flex: 3,
      editable: false,
      sortable: true,
      filterable: true,
      disableColumnMenu: false,
      renderCell: (vehicle) => {
        if (!vehicleId) {
          return vehicle.vehicleKey;
        }
      },
    },
    {
      field: "jobExecutionStatus",
      headerName: "Job Status",
      flex: 1,
      editable: false,
      sortable: true,
      filterable: true,
      disableColumnMenu: false,
      filterOperators: customFilterForJobStatus,
      renderCell: (data) => {
        const {
          row: {
            jobExecutionStatus = "",
            description = null,
            canceledAt = null,
            cancelReason = "",
          } = {},
        } = data || {};
        return (
          <ListItem
            sx={{
              cursor: "default",
            }}
          >
            <StatusTooltip
              title={getJobExecutionStatusTitle(cancelReason, canceledAt)}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -10],
                      },
                    },
                  ],
                },
              }}
              arrow
            >
              <ListItemText
                primary={<JobExecutionStatus status={jobExecutionStatus} />}
              ></ListItemText>
            </StatusTooltip>
          </ListItem>
        );
      },
    },
    {
      field: "vehicleGroupName",
      headerName: "Group Name",
      flex: 1,
      editable: false,
      sortable: true,
      filterable: true,
      disableColumnMenu: false,
      renderCell: (data) => {
        const { row: vehicle = {} } = data || {};
        if (!!vehicle?.vehicleGroupName && !!vehicle?.vehicleGroupId) {
          return (
            <Tooltip title={vehicle?.vehicleGroupName}>
              <Button
                disabled={vehicle?.vehicleGroupIsDeleted}
                size="medium"
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                }}
                component={RouterLink}
                to={`/groups/${vehicle.vehicleGroupId}/deployments/${deploymentId}`}
              >
                {vehicle?.vehicleGroupName}
              </Button>
            </Tooltip>
          );
        } else {
          return "-";
        }
      },
    },
    {
      field: "createdAt",
      headerName: "Started At",
      flex: 1,
      filterable: true,
      disableColumnMenu: false,
      sortable: true,
      renderCell: (vehicle) => {
        const { row: { createdAt = "" } = {} } = vehicle || {};
        return createdAt ? <DateAndTimeGridCell value={createdAt} /> : "-";
      },
    },
    {
      field: "updatedAt",
      headerName: "Last Updated At",
      flex: 1,
      filterable: true,
      disableColumnMenu: false,
      sortable: true,
      renderCell: (vehicle) => {
        const { row: { updatedAt = "" } = {} } = vehicle || {};
        return updatedAt ? <DateAndTimeGridCell value={updatedAt} /> : "-";
      },
    },
    {
      field: "none",
      headerName: "Actions",
      filterable: false,
      disableColumnMenu: false,
      sortable: false,
      flex: 1,
      renderCell: (data) => (
        <CancelVehicleDeployment
          data={data}
          deploymentVersion={deploymentVersion}
        />
      ),
    },
  ];
};

export { getColumnsData };
